import isEqual from 'lodash/isEqual';
import intersectionBy from 'lodash/intersectionBy';
import unionBy from 'lodash/unionBy';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import isNil from 'lodash/isNil';
import produce from 'immer';
import matchesProperty from 'lodash/matchesProperty';

import {CurrentProject} from 'common/report/queries/app-api/project-metadata-client';
import {ProjectDetails} from 'common/report/stores/project-details';
import {
  ProjectFeatureFlag,
  PropertyType,
  ResourceType,
  ResourceType as PropertyFilterResourceType,
} from 'common/report/constants';
import {customPropertiesStore} from 'common/report/stores/custom-properties';
import {CustomPropertyResourceType, Segmentation} from 'common/types/reports/query';
import {assert} from 'common/util';
import {isBehavioralCustomProperty, isPersistedCustomProperty} from 'common/widgets/custom-property-modal/util';
import {PropertyDefinition} from 'common/util/definitions-types';
import {propertiesUtils} from 'common/report/util/properties';
import {
  fetchLookupTableProperties,
  getTempLookupTableMappings,
  lookupTablesStore,
} from 'common/report/stores/lookup-tables';
import {assertUnreachable} from 'common/util/typescript';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {pluralize} from 'common/util/string';
import type {CohortStore} from 'common/report/stores/cohorts';
import {getSelectedPropertyName} from 'common/query-builder/query-block/utils/property-util';
import {getLabelForBehavior} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {
  getAllGlobalPropertyDefinitions,
  getAllPropertyDefinitionsForGroupProfiles,
  getAllPropertyDefinitionsForUserProfiles,
} from 'common/report/stores/property-definitions';
import {DataGroup} from 'common/widgets/data-group-selector/types';

import {
  DataGroupItem,
  isEventSectionItem,
  isProperty,
  Item,
  MenuItemType,
  NonDataGroupPropertyItemIdentifierKeys,
  Property,
  PropertyItemPersistedIdentifierKeys,
  SerializedItemKeyMap,
  StagedItemType,
} from './types';
import {EventSectionItem, ObjectProperty, PropertySectionItem} from './item-menu-types';
import {EXTRA_PROPERTY_DEFINITIONS_BY_TYPE} from './constants';

/**
 * Find intersection of Mixpanel properties present in all given property lists.
 * Checks equality via `name` prop.
 */
export function calculatePropertiesIntersection(propertyLists: PropertySectionItem[]) {
  // @ts-ignore - TODO: Argument of type 'PropertyItem' is not assignable to parameter of type 'ArrayLike<string>'.
  return intersectionBy(...propertyLists, `name`);
}

/**
 * Find union of Mixpanel properties present in all given property lists.
 * Checks equality via `name` prop.
 */
export function calculatePropertiesUnion(propertyLists: PropertySectionItem[]) {
  // @ts-ignore - TODO: Expected at least 1 arguments, but got 1 or more.
  return unionBy(...propertyLists, `name`);
}

export function sanitizeCustomProperty(customProperty) {
  return pick(customProperty, [
    `behavior`,
    `composedProperties`,
    `mappedDataGroupId`,
    `referencedDirectlyBy`,
    `referencedBy`,
    `referencedRawEventProperties`,
    `description`,
    `displayFormula`,
    `name`,
    `resourceType`,
    `propertyType`,
    `joinPropertyType`,
    `allow_staff_override`,
    `canUpdateBasic`,
    `can_share`,
    `is_superadmin`,
  ]);
}

export const CUSTOM_PROPERTY_RESOURCE_TYPE_MAPPING = {
  [CustomPropertyResourceType.Events]: PropertyFilterResourceType.Event,
  [CustomPropertyResourceType.People]: PropertyFilterResourceType.User,
};

export function storeCustomPropertyToProperty(customPropertyId): PropertySectionItem {
  let customProperty = customPropertiesStore.value[customPropertyId];
  customProperty = sanitizeCustomProperty(customProperty);
  const {name} = customProperty;
  let {mappedDataGroupId, propertyType, joinPropertyType} = customProperty;

  let resourceType = CUSTOM_PROPERTY_RESOURCE_TYPE_MAPPING[customProperty.resourceType] || null;
  if (isBehavioralCustomProperty(customProperty)) {
    /** Behavioral Custom Properties are always User Properties */
    resourceType = ResourceType.User;
  }

  if (isPersistedCustomProperty(customPropertyId)) {
    customPropertyId = Number(customPropertyId);
    const tempDataGroupId = getTempLookupTableMappings(resourceType, true)?.get(customPropertyId);
    if (tempDataGroupId) {
      mappedDataGroupId = tempDataGroupId;
      joinPropertyType = joinPropertyType || propertyType;
      propertyType = PropertyType.Dimension;
    }
  }

  let menuItemType;
  switch (resourceType) {
    case ResourceType.Event:
      menuItemType = MenuItemType.EventCustomProperties;
      break;
    case ResourceType.User:
      menuItemType = MenuItemType.UserCustomProperties;
      break;
    case ResourceType.Cohort:
    case ResourceType.Other:
    case ResourceType.Formula:
      throw Error(`Unexpected resource type in storeCustomPropertyToProperty: ${resourceType}`);
    case null:
      // TODO: This should really not happen and code should be cleaned up to ensure it doesn't
      menuItemType = MenuItemType.EventCustomProperties;
      break;
    default:
      assertUnreachable(resourceType);
  }

  return {
    customProperty,
    customPropertyId,
    dimensionDataGroupId: mappedDataGroupId,
    resourceType,
    propertyType,
    propertyDefaultType: propertyType,
    type: propertyType,
    joinPropertyType,
    value: name,
    label: name,
    menuItemType,
  };
}

/**
 * Compare if two properties are the same, ignoring typecast, changes in custom property definitions, etc...
 * NOTE: Even for inline custom properties, they must have the same temporary id to be considered the same.
 */
export function isSamePropertyReference(property1, property2): boolean {
  if (property1.customPropertyId || property2.customPropertyId) {
    return property1.customPropertyId === property2.customPropertyId;
  }
  const PROPERTY_KEY_FIELDS = [`resourceType`, `propertyName`];
  return isEqual(pick(property1, PROPERTY_KEY_FIELDS), pick(property2, PROPERTY_KEY_FIELDS));
}

/** @todo: [shawn] Join utils to a base type convertor */
export function propertyItemToObjectPropertyItem(propertyItem: PropertySectionItem): ObjectProperty {
  assert(isProperty(propertyItem));
  const {
    dataGroupId,
    resourceType,
    propertyDefaultType,
    propertyObjectKey,
    tempDataGroupId,
    joinPropertyType,
    type,
    value,
  } = propertyItem;

  return {
    ...(dataGroupId ? {dataGroupId} : {}),
    ...(joinPropertyType ? {joinPropertyType} : {}),
    ...(tempDataGroupId ? {tempDataGroupId} : {}),
    type,
    resourceType,
    propertyDefaultType,
    propertyName: value,
    propertyType: type,
    ...(propertyObjectKey
      ? {
          propertyObjectKey,
        }
      : {}),
  };
}

/**
 * Generates a Segmentation format for user or event PropertyItem types
 * used in property menu. In general, Segmentation is what we use for bookmarks.
 *
 * TODO: Strongly type the input, theres a bug in the type of propertyItem
 * not transferring custom properties correctly.
 */
export function propertyItemToSegmentation(propertyItem, resourceType): Segmentation {
  const {dimensionDataGroupId, dataGroupId, value, type} = propertyItem;

  return {
    resourceType,
    propertyType: type,
    propertyName: value,
    ...(dataGroupId ? {dataGroupId} : dimensionDataGroupId && Array.isArray(value) ? {dimensionDataGroupId} : null),
    ...pick(
      propertyItem,
      `behavior`,
      `customPropertyId`,
      `customProperty`,
      `tempDataGroupId`,
      `propertyDefaultType`,
      `propertyObjectKey`,
      `joinPropertyType`,
      `listItemGroup`,
      `listItemFilters`,
      `unit`,
      `isScd`,
    ),
  };
}

/**
 * Historically, we have been pushing 2 formats for defining properties
 * in the UI, as a result we have Segmentation and Property which are
 * almost identical with the exception of the following fields:
 *
 * `propertyType` <-> `type`
 *
 * `propertyName` <-> `value
 */
export function segmentationToPropertyItem(segment: Segmentation): Property {
  return {
    ...omit(segment, `propertyName`, `propertyType`),
    type: segment.propertyType,
    value: segment.propertyName,
  };
}

export function getDataDefinitionsPropertyMenuItem(propertyDefinition: PropertyDefinition) {
  return {
    ...propertyDefinition,
    label: propertiesUtils.renameProperty(propertyDefinition.name, propertyDefinition.resourceType),
    value: propertyDefinition.name,
    ...(propertyDefinition.join_property_type ? {joinPropertyType: propertyDefinition.join_property_type} : {}),
    ...(propertyDefinition.dimension_data_group_id
      ? {dimensionDataGroupId: propertyDefinition.dimension_data_group_id}
      : {}),
  };
}

export function isSameProperty(property1: PropertySectionItem, property2: PropertySectionItem): boolean {
  return getItemUniqueKey(property1) === getItemUniqueKey(property2);
}

export function getItemUniqueKey(item: EventSectionItem | PropertySectionItem | Item) {
  // ignore tempDataGroupId and dataGroupId when it's a join key
  const primaryKeys =
    `type` in item && item.type === `dimension`
      ? NonDataGroupPropertyItemIdentifierKeys
      : PropertyItemPersistedIdentifierKeys;
  const primaryKey = primaryKeys.find((key) => Boolean(item[key]));

  if (!(`resourceType` in item) && isEventSectionItem(item)) {
    return item.value.value;
  } else {
    const {resourceType = ``, value} = item;
    let tempDataGroupId, customPropertyId, dataGroupId;
    if (isProperty(item)) {
      ({tempDataGroupId, customPropertyId = ``, dataGroupId} = item);
    }
    switch (primaryKey) {
      case `tempDataGroupId`:
        return `temp${tempDataGroupId}::${customPropertyId}::${resourceType}::${value}`;
      case `dataGroupId`:
        return `${dataGroupId}::${customPropertyId}::${resourceType}::${value}`;
      case `customPropertyId`:
        return String(customPropertyId);
      default:
        return `${resourceType}::${item[primaryKey]}`;
    }
  }
}

export function inferStagedItemType(item: PropertySectionItem, useMultipleSelection: boolean): StagedItemType {
  if (!isProperty(item)) {
    return null;
  }
  if (!useMultipleSelection) {
    if (item.menuItemType === MenuItemType.ComputedListPropertySum) {
      return StagedItemType.ComputedListNumericProperty;
    }

    if (item.menuItemType === MenuItemType.ComputedListPropertyUniques) {
      return StagedItemType.ComputedListProperty;
    }

    if (!item.unit && item.type === PropertyType.Datetime) {
      return StagedItemType.Datetime;
    }

    if (item.type === PropertyType.Dimension) {
      return StagedItemType.Dimension;
    }

    if (item.menuItemType === MenuItemType.BehaviorMultiAttribution) {
      return StagedItemType.BehaviorMultiAttribution;
    }

    if (item.menuItemType === MenuItemType.EventWithProperties) {
      return StagedItemType.EventWithProperties;
    }

    if (
      item.type === PropertyType.Object &&
      item.propertyDefaultType === PropertyType.Object &&
      !item.propertyObjectKey?.length
    ) {
      return StagedItemType.Object;
    }
  }

  if (!isNil(item.stagedItemType)) {
    return item.stagedItemType;
  }
  return null;
}

export function filterToBookmarkablePropertyItem(item: PropertySectionItem): Item {
  return produce(item, (itemDraft: PropertySectionItem): void => {
    for (const key in itemDraft) {
      if (!SerializedItemKeyMap[key]) {
        delete itemDraft[key];
      }
    }
  });
}

function resourceTypeLabelForPath(resourceType: Property['resourceType'], dataGroup: DataGroupItem | null) {
  switch (resourceType) {
    case ResourceType.User:
      return dataGroup?.displayName || `User`;
    case ResourceType.Event:
    case ResourceType.Cohort:
    case ResourceType.Other:
    case ResourceType.Formula:
    case null:
      return null;
    default:
      assertUnreachable(resourceType);
  }
}

export function getTooltipPathForPropertyItem(
  {value, resourceType}: Property,
  dataGroup: DataGroupItem | null,
): string[] {
  return [
    resourceTypeLabelForPath(resourceType, dataGroup),
    Array.isArray(value) && propertiesUtils.renameProperty(value[0], resourceType),
  ].filter(Boolean);
}

export function getTooltipNameForPropertyItem({value}: Property): string {
  return Array.isArray(value) ? value[1] : value;
}

/**
 * This function is to ensure consistency between the properties-menu fetch
 * call and the pre-fetch call made in query-builder
 * */
export function getEventPropertyDefinitionsParams(
  {
    projectId,
    workspaceId,
    event,
    includeDimensionProperties,
  }: {projectId: string | number; workspaceId: string | number; event: string[]; includeDimensionProperties: boolean},
  includeAllProperties = false,
) {
  return {
    ...(includeAllProperties ? {} : {event}),
    projectId,
    workspaceId,
    includeDimensionProperties,
    includeHidden: true,
    includeDensity: true,
  };
}

// This function fetches and populates the Lookup Table Store with each table's properties
export function fetchAllLookupTableProperties() {
  const projectId = getProjectId();
  const workspaceId = getWorkspaceId();
  for (const table of Object.values(lookupTablesStore.value)) {
    if (table.hasMappedProperties) {
      const dataGroupId = table[`id`];
      fetchLookupTableProperties(dataGroupId, projectId, workspaceId);
    }
  }
}

export function getSelectedPropertyLabel({
  selected,
  showSpecialDatePropertyUnit = false,
  cohortsStore,
  dataGroupId = null,
  dataGroups = null,
  expandListProperties = true,
}: {
  selected: TODO;
  showSpecialDatePropertyUnit?: boolean;
  cohortsStore?: CohortStore;
  dataGroupId?: string;
  dataGroups?: DataGroup[];
  expandListProperties?: boolean;
}) {
  if (selected && selected.renamed) {
    return `${selected.renamed} (${selected.label})`;
  } else if (selected && !selected.value && selected.label) {
    return selected.label;
  } else if (selected?.behavior) {
    return getLabelForBehavior(selected.behavior, dataGroupId, dataGroups);
  } else {
    const firstItem = Array.isArray(selected) ? selected[0] : selected;

    if (!firstItem) {
      return ``;
    }

    // use canonical cohort names when available
    if (firstItem.resourceType === ResourceType.Cohort) {
      if (Array.isArray(selected)) {
        const length = selected.length;
        return `${length} ${pluralize(`cohort`, length)}`;
      }

      const label = cohortsStore?.value[firstItem.value]?.name || firstItem.label;
      return firstItem.negated ? `Not in ${label}` : label;
    }

    return getSelectedPropertyName({
      selected,
      showSpecialDatePropertyUnit,
      expandListProperties,
      dataGroupId,
    });
  }
}

/**
 * @deprecated
 * This is currently only used to getTooltip data.
 * We should be able to refactor this out soon.
 */
export function getPropertyDefinitions(resourceTypes: ResourceType[]) {
  return resourceTypes.reduce((acc, type) => {
    let definitions = [];
    switch (type) {
      case ResourceType.Event:
        // ? Query block is only able to edit global definitions
        // ? we need to keep serving tooltip data out of global definitions until this changes
        definitions = getAllGlobalPropertyDefinitions();
        break;
      case ResourceType.User:
        definitions = getAllPropertyDefinitionsForUserProfiles().concat(getAllPropertyDefinitionsForGroupProfiles());
        break;
    }

    const inlineCustomPropertyIds = Object.keys(customPropertiesStore.value).filter((customPropertyId) => {
      return (
        !isPersistedCustomProperty(customPropertyId) &&
        !customPropertiesStore.value[customPropertyId].newCustomPropertyId
      );
    });

    const customPropertyIdSet = new Set(definitions.map((d) => d.customPropertyId).filter((id) => Boolean(id)));
    const createdCustomPropertyIds = Object.keys(customPropertiesStore.value).filter(
      (customPropertyId) => isPersistedCustomProperty(customPropertyId) && !customPropertyIdSet.has(customPropertyId),
    );

    // add in definitions from newly created and inline custom properties
    const customDefinitions = [...createdCustomPropertyIds, ...inlineCustomPropertyIds]
      .map((customPropertyId) => {
        const property = storeCustomPropertyToProperty(customPropertyId);
        const definition = customPropertiesStore.value[customPropertyId];
        return {
          ...property,
          name: definition?.name,
          description: definition?.description,
          status: `custom`,
        } as PropertyDefinition;
      })
      .filter(matchesProperty(`resourceType`, type));

    const regularDefinitions = definitions.filter((definition) => definition.status !== `custom`);

    acc[type] = [...customDefinitions, ...regularDefinitions, ...EXTRA_PROPERTY_DEFINITIONS_BY_TYPE[type]];

    return acc;
  }, {});
}

export function canUseSlowlyChangingDimensions(currentProject?: CurrentProject | ProjectDetails) {
  // Always return true for projects with feature flag enabled
  return currentProject?.hasFeatureFlag(ProjectFeatureFlag.SlowlyChangingDimensionsPoc);
}
